// Typed fetch wrapper. One module talks to the backend; pages and components
// import named functions, not raw fetch. Auth header is injected from
// localStorage so any page can call any endpoint without thinking about it.

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

const TOKEN_STORAGE_KEY = "scholarpetition.token";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setToken(token: string | null) {
  if (token === null) localStorage.removeItem(TOKEN_STORAGE_KEY);
  else localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function authToken(): string | null {
  return getToken();
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; auth?: boolean } = {}
): Promise<T> {
  const { method = "GET", body, auth = true } = options;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get("content-type") ?? "";
  const parsed: unknown = contentType.includes("application/json")
    ? await res.json()
    : await res.text();
  if (!res.ok) {
    const detail =
      typeof parsed === "object" && parsed !== null && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    const message =
      typeof detail === "string" ? detail : `Request failed (${res.status})`;
    throw new ApiError(res.status, parsed, message);
  }
  return parsed as T;
}

// -- Types matching the FastAPI schemas --

export interface HealthOut {
  status: "ok" | "degraded";
  version: string;
  environment: string;
  demo_mode: boolean;
  email_configured: boolean;
}

export const health = {
  get: () => request<HealthOut>("/v1/healthz", { auth: false }),
};

// Public attorney-share endpoints — reached via token, no auth required.
export const share = {
  caseSummary: (token: string) =>
    request<CaseSummaryOut>(`/v1/share/${token}/case-summary`, { auth: false }),
  packetUrl: (token: string) => `${API_BASE_URL}/v1/share/${token}/packet`,
};

export interface TenantOut {
  id: string;
  slug: string;
  display_name: string;
  kind: "scholar" | "attorney" | "partner_workspace";
  distribution_channel: "direct" | "partner";
  partner_slug: string | null;
  is_marketplace_listed: boolean;
  config: Record<string, unknown>;
  subscription_status: string;
  is_paying: boolean;
  has_stripe_customer: boolean;
}

export const tenants = {
  me: () => request<TenantOut>("/v1/tenants/me"),
};

export interface TokenOut {
  access_token: string;
  token_type: string;
  expires_at: string;
  tenant_id: string;
  tenant_display_name: string;
  user_email: string;
  role: string;
}

export interface ScholarProfileOut {
  id: string;
  full_name: string;
  preferred_name: string | null;
  email: string | null;
  country_of_birth: string | null;
  country_of_nationality: string | null;
  current_institution: string | null;
  current_title: string | null;
  field_of_study: string | null;
  education_history: Array<Record<string, unknown>>;
  employment_history: Array<Record<string, unknown>>;
  notes: string | null;
  google_scholar_user_id: string | null;
}

export interface ApplicationOut {
  id: string;
  application_type: "eb1a" | "eb2_niw";
  status: string;
  title: string;
  scholar_profile_id: string;
  subtype_data: Record<string, unknown>;
}

export interface EvidenceOut {
  id: string;
  application_id: string;
  kind: string;
  label: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  parse_status: string;
  parse_error: string | null;
  parsed_metadata: Record<string, unknown>;
  download_url: string | null;
  prestige_url: string | null;
}

export interface CriterionAssessmentOut {
  id: string;
  application_id: string;
  criterion_key: string;
  score: "strong" | "moderate" | "weak" | "missing";
  rationale: string | null;
  supporting_evidence_ids: string[];
  extra: Record<string, unknown>;
}

export interface CriterionCatalogEntry {
  key: string;
  short_title: string;
  description: string;
  citation: string;
}

export interface RecommenderOut {
  id: string;
  full_name: string;
  title: string | null;
  institution: string | null;
  country: string | null;
  email: string | null;
  relationship: "independent" | "dependent";
  expertise_summary: string | null;
  /** Independent-citation signal — drives Letter Studio's
   *  "Cited your work N×" arms-length overline. Null when unknown. */
  cited_count: number | null;
  /** Free-text explanation of the relationship signal
   *  ("Reviewed your 2024 Nature paper", "Co-author on 3 papers").
   *  Falls back to expertise_summary or a generic group line. */
  relationship_evidence: string | null;
}

export interface ProposedEditOut {
  id: string;
  old_text: string;
  new_text: string;
  reason: string;
}

export interface SuggestEditOut {
  summary: string;
  edits: ProposedEditOut[];
}

export interface RecommendationLetterOut {
  id: string;
  application_id: string;
  recommender_id: string;
  status: string;
  body_markdown: string | null;
  has_docx: boolean;
  // Publication UUIDs the user has anchored this letter on. Empty list
  // means the backend's auto-pick rule will run at draft time.
  anchor_publication_ids: string[];
  // User-selected tone preset. Null = backend default
  // (knowledgeable_friend).
  tone: "knowledgeable_friend" | "formal_expert" | "direct_concise" | null;
  // Recommender outreach loop. Null until the scholar clicks
  // "Send to Prof. X for approval" / the recommender returns the letter.
  outreach_sent_at: string | null;
  recommender_returned_text: string | null;
  has_signed_pdf: boolean;
  received_at: string | null;
  created_at: string | null;
}

export interface RecommenderEnrichExtracted {
  title: string | null;
  institution: string | null;
  focus: string | null;
  bio: string | null;
  h_index: number | null;
  citations_all: number | null;
}

export interface RecommenderEnrichResponse {
  recommender: RecommenderOut;
  extracted: RecommenderEnrichExtracted;
  source: "scholar" | "web" | "cache" | "cv";
}

export interface RecommenderSuggestion {
  name: string;
  affiliations: string[];
  evidence_text: string;
  kind: "collaborator" | "independent";
  s2_url: string | null;
  most_recent_year: number | null;
  sample_paper_title: string | null;
}

export interface RecommenderSuggestionsOut {
  collaborators: RecommenderSuggestion[];
  independents: RecommenderSuggestion[];
  no_data: boolean;
  reason: string | null;
  /** Soft callout shown next to the arms-length section when
   *  collaborators surfaced but independents did not — explains why
   *  every citer got filtered out (most often "all citers also
   *  co-authored with you"). */
  independents_reason: string | null;
}

export interface PetitionLetterOut {
  id: string;
  application_id: string;
  status: string;
  body_markdown: string | null;
  has_docx: boolean;
}

// -- API surface --

export const auth = {
  demoLogin: (body: {
    email: string;
    workspace_name: string;
    role?: string;
    kind?: string;
    referral_code?: string;
  }) => request<TokenOut>("/v1/auth/demo-login", { method: "POST", body, auth: false }),
  signIn: (body: { email: string; workspace_name?: string; referral_code?: string }) =>
    request<{ message: string }>("/v1/auth/sign-in", {
      method: "POST",
      body,
      auth: false,
    }),
  acceptInvite: (token: string, referral_code?: string) =>
    request<TokenOut>("/v1/auth/accept-invite", {
      method: "POST",
      body: { token, ...(referral_code ? { referral_code } : {}) },
      auth: false,
    }),
  verifyCode: (body: { email: string; code: string; referral_code?: string }) =>
    request<TokenOut>("/v1/auth/verify-code", {
      method: "POST",
      body,
      auth: false,
    }),
  google: (body: { credential: string; workspace_name?: string; referral_code?: string }) =>
    request<TokenOut>("/v1/auth/google", {
      method: "POST",
      body,
      auth: false,
    }),
};

// Refer-a-friend ---------------------------------------------------------
export interface ReferralOut {
  id: string;
  referee_name: string;
  referee_email: string;
  status: "sent" | "signed_up" | "checked_out" | "expired";
  created_at: string;
  signed_up_at: string | null;
  checked_out_at: string | null;
}

export interface ReferralListOut {
  referrals: ReferralOut[];
  credit_cents: number;
  credit_label: string;
}

export const referrals = {
  create: (body: { referee_name: string; referee_email: string }) =>
    request<ReferralOut>("/v1/referrals", { method: "POST", body }),
  list: () => request<ReferralListOut>("/v1/referrals"),
};

export interface LeadIn {
  name: string;
  email: string;
  phone?: string | null;
  requested_slot?: string | null;
  stage?: string | null;
  note?: string | null;
  source?: string;
  referral_code?: string | null;
}

export interface LeadOut {
  id: string;
  created_at: string;
  message: string;
}

export const leads = {
  create: (body: LeadIn) => request<LeadOut>("/v1/leads", { method: "POST", body }),
};

export interface SeedDemoResult {
  scholar_profile_id: string;
  application_id: string;
  publications_inserted: number;
  evidence_inserted: number;
  recommenders_inserted: number;
  assessments_inserted: number;
}

export const scholarProfile = {
  /** Wipe the caller's ScholarProfile + everything cascaded below it.
   *  Idempotent: returns 204 even when no profile exists. Backs the
   *  "Replay the flow from the start" affordance on the Case Plan. */
  reset: () =>
    request<void>("/v1/scholar-profile/reset", { method: "POST" }),
  get: () => request<ScholarProfileOut>("/v1/scholar-profile"),
  upsert: (body: Partial<ScholarProfileOut> & { full_name: string }) =>
    request<ScholarProfileOut>("/v1/scholar-profile", { method: "PUT", body }),
  seedDemo: () =>
    request<SeedDemoResult>("/v1/scholar-profile/seed-demo", { method: "POST" }),
};

// Guided Scholar Path (EB-1A) — backs the card flow at /scholar-path.
export interface EB1AProfileOut {
  education: string[];
  employment: string[];
  awards: string[];
  patents: string[];
  peer_review: string[];
  presentations: string[];
  publications: string[];
  memberships: string[];
  press: string[];
}

export interface EB1ACriterionEvidence {
  id: string;
  strength: "strong" | "moderate" | "building" | "absent";
  evidenceCount: number;
  cvHits: string[];
  scholarHits: string[];
}

export const eb1a = {
  profile: () => request<EB1AProfileOut>("/v1/eb1a/profile"),
  evidence: () => request<EB1ACriterionEvidence[]>("/v1/eb1a/evidence"),
  saveAnswer: (criterionId: string, hasEvidence: boolean) =>
    request<{ answers: Record<string, boolean> }>("/v1/eb1a/answers", {
      method: "POST",
      body: { criterionId, hasEvidence },
    }),
  connectSource: (provider: "gdrive" | "dropbox" | "onedrive" | "box") =>
    request<{ connected: boolean; reason: string | null; auth_url: string | null }>(
      "/v1/eb1a/sources/connect",
      { method: "POST", body: { provider } },
    ),
};

export interface PaidStatusOut {
  paid: boolean;
  paywall_enabled: boolean;
}

export interface CheckoutResponse {
  url: string;
  session_id: string;
}

export interface CaseSummaryOut {
  display_name: string;
  field: string;
  institution: string;
  nationality: string;
  current_status: string;
  classification: string;
  title: string;
  status: string;
  concurrent_i485: string;
  premium_processing: string;
  paid_at: string | null;
  signed_letters: string[];
  draft_letters: string[];
  pending_letters: string[];
  evidence_by_kind: Record<string, number>;
  gaps: string[];
}

export interface ShareLinkOut {
  token: string;
  url: string;
  expires_in_days: number;
}

export const applications = {
  list: () => request<ApplicationOut[]>("/v1/applications"),
  get: (id: string) => request<ApplicationOut>(`/v1/applications/${id}`),
  paidStatus: (id: string) =>
    request<PaidStatusOut>(`/v1/applications/${id}/paid`),
  caseSummary: (id: string) =>
    request<CaseSummaryOut>(`/v1/applications/${id}/case-summary`),
  share: (id: string) =>
    request<ShareLinkOut>(`/v1/applications/${id}/share`, { method: "POST" }),
  checkout: (id: string) =>
    request<CheckoutResponse>(`/v1/applications/${id}/checkout`, {
      method: "POST",
    }),
  create: (body: { application_type: "eb1a" | "eb2_niw"; title: string }) =>
    request<ApplicationOut>("/v1/applications", { method: "POST", body }),
  patch: (id: string, body: Partial<{ title: string; status: string; subtype_data: Record<string, unknown> }>) =>
    request<ApplicationOut>(`/v1/applications/${id}`, { method: "PATCH", body }),
  /** URL for downloading the watermarked draft I-140 PDF. */
  i140PdfUrl: (id: string) => `${API_BASE_URL}/v1/applications/${id}/i140.pdf`,
  /** URL for downloading the watermarked draft I-907 PDF. */
  i907PdfUrl: (id: string) => `${API_BASE_URL}/v1/applications/${id}/i907.pdf`,
  /** URL for the free pre-paywall I-140 preview (loud watermark). */
  i140PreviewPdfUrl: (id: string) => `${API_BASE_URL}/v1/applications/${id}/i140-preview.pdf`,
  /** Top-N SOC matches for the I-140 occupation classification field.
   *  Reads scholar profile (current_title + field_of_study + degrees)
   *  on the server and runs the BLS OEWS fuzzy matcher. Returns an
   *  empty list when the profile is too sparse to score anything. */
  socMatch: (id: string, topN = 3) =>
    request<{
      matches: {
        soc: string;
        title: string;
        score: number;
        national_median: number;
        p10: number;
        p90: number;
      }[];
    }>(`/v1/applications/${id}/soc-match?top_n=${topN}`),
};

export const evidence = {
  list: (applicationId: string) =>
    request<EvidenceOut[]>(`/v1/applications/${applicationId}/evidence`),
  upload: async (
    applicationId: string,
    file: File,
    kind: string,
    label?: string,
    prestige_url?: string,
  ): Promise<EvidenceOut> => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    if (label) fd.append("label", label);
    if (prestige_url) fd.append("prestige_url", prestige_url);
    const token = getToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/applications/${applicationId}/evidence`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      }
    );
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        parsed,
        `Upload failed (${res.status})`
      );
    }
    return (await res.json()) as EvidenceOut;
  },
  patch: (evidenceId: string, body: { kind?: string; label?: string; prestige_url?: string }) =>
    request<EvidenceOut>(`/v1/evidence/${evidenceId}`, { method: "PATCH", body }),
  delete: (evidenceId: string) =>
    request<void>(`/v1/evidence/${evidenceId}`, { method: "DELETE" }),
};

export const criteria = {
  catalog: (applicationType: "eb1a" | "eb2_niw") =>
    request<CriterionCatalogEntry[]>(
      `/v1/criteria/catalog?application_type=${applicationType}`,
      { auth: false }
    ),
  list: (applicationId: string) =>
    request<CriterionAssessmentOut[]>(
      `/v1/applications/${applicationId}/criteria`
    ),
  recompute: (applicationId: string) =>
    request<CriterionAssessmentOut[]>(
      `/v1/applications/${applicationId}/criterion-mapping`,
      { method: "POST" }
    ),
};

export const recommenders = {
  list: () => request<RecommenderOut[]>("/v1/recommenders"),
  create: (body: {
    full_name: string;
    title?: string;
    institution?: string;
    country?: string;
    email?: string;
    relationship: "independent" | "dependent";
    expertise_summary?: string;
    cited_count?: number | null;
    relationship_evidence?: string | null;
  }) =>
    request<RecommenderOut>("/v1/recommenders", { method: "POST", body }),
  update: (
    id: string,
    body: {
      full_name?: string;
      title?: string;
      institution?: string;
      country?: string;
      email?: string;
      relationship?: "independent" | "dependent";
      expertise_summary?: string;
      cited_count?: number | null;
      relationship_evidence?: string | null;
    }
  ) => request<RecommenderOut>(`/v1/recommenders/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    request<void>(`/v1/recommenders/${id}`, { method: "DELETE" }),
  letters: (applicationId: string) =>
    request<RecommendationLetterOut[]>(
      `/v1/applications/${applicationId}/recommendation-letters`
    ),
  patchLetter: (
    applicationId: string,
    letterId: string,
    body: {
      body_markdown?: string;
      status?: string;
      anchor_publication_ids?: string[];
      tone?: "knowledgeable_friend" | "formal_expert" | "direct_concise" | "";
    }
  ) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}`,
      { method: "PATCH", body }
    ),
  /** Fetch a profile/lab/institution URL and populate the recommender's
   *  title / institution / expertise_summary from what's extracted. */
  enrich: (recommenderId: string, url: string) =>
    request<RecommenderEnrichResponse>(
      `/v1/recommenders/${recommenderId}/enrich`,
      { method: "POST", body: { url } }
    ),
  /** Upload a recommender CV (PDF) and populate title/institution/
   *  expertise_summary from the extracted text. The PDF is NOT stored. */
  uploadCv: async (
    recommenderId: string,
    file: File,
  ): Promise<RecommenderEnrichResponse> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = authToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/recommenders/${recommenderId}/cv-upload`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      },
    );
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      const detail =
        typeof parsed === "object" && parsed && "detail" in parsed
          ? (parsed as { detail: unknown }).detail
          : null;
      throw new ApiError(
        res.status,
        parsed,
        typeof detail === "string" ? detail : `Upload failed (${res.status})`,
      );
    }
    return (await res.json()) as RecommenderEnrichResponse;
  },
  /** 4 collaborators + 4 arms-length recommender suggestions, computed
   *  from the user's publications via the Semantic Scholar graph.
   *  Cached server-side for 24h on subtype_data so a repeat visit
   *  doesn't re-hit S2. Pass refresh=true (the StepWriter Retry button)
   *  to bypass the cache. */
  suggestions: (applicationId: string, refresh = false) =>
    request<RecommenderSuggestionsOut>(
      `/v1/applications/${applicationId}/recommender-suggestions${refresh ? "?refresh=true" : ""}`
    ),
  /** Conversational edit — propose span-anchored edits from a natural-language
   *  instruction. Returns a summary + list of { old_text, new_text } edits
   *  the frontend renders as an inline diff with per-change Accept/Reject. */
  suggestEdit: (
    applicationId: string,
    letterId: string,
    body: { instruction: string; current_text: string }
  ) =>
    request<SuggestEditOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}/suggest-edit`,
      { method: "POST", body }
    ),
  /** Email a magic-link review request to the recommender. */
  sendOutreach: (recommenderId: string, letterId: string) =>
    request<{ ok: boolean; message_id: string; magic_link: string; sent_to: string }>(
      `/v1/recommenders/${recommenderId}/letters/${letterId}/send-outreach`,
      { method: "POST" }
    ),
  /** Scholar approves a drafted letter (status → SCHOLAR_EDITED). Called
   *  by Letter Studio's "Download & next letter" CTA: the download itself
   *  implies the scholar is taking the draft offline. */
  approveLetter: (applicationId: string, letterId: string) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}/approve`,
      { method: "POST" }
    ),
  /** Scholar marks a letter as sent to the recommender offline (status
   *  → SENT_TO_RECOMMENDER, outreach_sent_at = now). */
  markSent: (applicationId: string, letterId: string) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}/mark-sent`,
      { method: "POST" }
    ),
  /** URL for the bulk-download endpoint that returns every drafted
   *  letter zipped together. Caller fetches with the Authorization
   *  header (no SSE query-token fallback needed). */
  bulkDocxUrl: (applicationId: string) =>
    `${API_BASE_URL}/v1/applications/${applicationId}/recommendation-letters/bulk-docx`,
  /** Letter Studio "Start over" — drop every drafted letter for the
   *  application so the returning-user resume effect doesn't snap
   *  the user back into the batch-review screen. Recommender rows
   *  stay; only the letter rows go. */
  deleteAllLetters: (applicationId: string) =>
    request<void>(
      `/v1/applications/${applicationId}/recommendation-letters/all`,
      { method: "DELETE" }
    ),
  /** Scholar uploads the signed PDF (status → SIGNED, received_at = now).
   *  Mirrors the recommender-magic-link path but takes scholar auth. */
  uploadSignedLetter: async (
    applicationId: string,
    letterId: string,
    file: File,
  ): Promise<RecommendationLetterOut> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = authToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/applications/${applicationId}/recommendation-letters/${letterId}/upload-signed`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      },
    );
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      throw new ApiError(res.status, parsed, `Upload failed (${res.status})`);
    }
    return (await res.json()) as RecommendationLetterOut;
  },
  /** URL for the scholar to download the signed PDF the recommender returned. */
  signedPdfUrl: (recommenderId: string, letterId: string) =>
    `${API_BASE_URL}/v1/recommenders/${recommenderId}/letters/${letterId}/signed-pdf`,
};

export const petitionLetter = {
  get: (applicationId: string) =>
    request<PetitionLetterOut>(
      `/v1/applications/${applicationId}/petition-letter`
    ),
  patch: (
    applicationId: string,
    body: { body_markdown?: string; status?: string }
  ) =>
    request<PetitionLetterOut>(
      `/v1/applications/${applicationId}/petition-letter`,
      { method: "PATCH", body }
    ),
};

export interface PublicationOut {
  id: string;
  scholar_profile_id: string;
  title: string;
  venue: string | null;
  year: number | null;
  authors: string | null;
  scholar_role: string | null;
  citation_count: number | null;
  doi: string | null;
  url: string | null;
}

export interface PublicationIn {
  title: string;
  venue?: string | null;
  year?: number | null;
  authors?: string | null;
  scholar_role?: string | null;
  citation_count?: number | null;
  doi?: string | null;
  url?: string | null;
}

// Mirrors ScholarFetchOut in the backend schema. Identical shape to
// ScholarParseResult so it drops into the same review screen unchanged.
export interface ScholarFetchPubOut {
  title: string;
  authors: string | null;
  venue: string | null;
  year: number | null;
  citation_count: number | null;
  scholar_url: string | null;
  authorship_role: "first" | "last" | "middle" | "solo" | "unknown" | null;
}

export interface ScholarFetchOut {
  user_id: string;
  display_name: string | null;
  affiliation: string | null;
  citations_all: number | null;
  h_index: number | null;
  i10_index: number | null;
  publications: ScholarFetchPubOut[];
}

export interface ScholarImportOut {
  user_id: string;
  display_name: string | null;
  affiliation: string | null;
  citations_all: number | null;
  h_index: number | null;
  i10_index: number | null;
  publications_inserted: number;
}

export const publications = {
  list: () => request<PublicationOut[]>("/v1/publications"),
  create: (body: PublicationIn) =>
    request<PublicationOut>("/v1/publications", { method: "POST", body }),
  update: (id: string, body: PublicationIn) =>
    request<PublicationOut>(`/v1/publications/${id}`, {
      method: "PATCH",
      body,
    }),
  remove: (id: string) =>
    request<void>(`/v1/publications/${id}`, { method: "DELETE" }),
  bulkImport: (rows: PublicationIn[]) =>
    request<PublicationOut[]>("/v1/publications/bulk-import", {
      method: "POST",
      body: rows,
    }),
  /** One-shot: fetch Scholar, clear old pubs, insert new ones server-side. */
  scholarImport: (scholarUrl: string) =>
    request<ScholarImportOut>("/v1/publications/scholar-import", {
      method: "POST",
      body: { scholar_url: scholarUrl },
    }),
  fetchScholar: (scholarUrl: string) =>
    request<ScholarFetchOut>("/v1/publications/scholar-fetch", {
      method: "POST",
      body: { scholar_url: scholarUrl },
    }),
  clearAll: () =>
    request<void>("/v1/publications", { method: "DELETE" }),
  dedupe: () =>
    request<{ removed: number; kept: number }>("/v1/publications/dedupe", {
      method: "POST",
    }),
};

export interface MarketplaceAttorneyOut {
  slug: string;
  display_name: string;
  profile: Record<string, string>;
}

export interface EngagementOut {
  id: string;
  scholar_tenant_id: string;
  attorney_tenant_id: string;
  application_id: string;
  status: string;
  scholar_message: string | null;
  attorney_proposal: string | null;
  attorney_fee_quote_cents: number | null;
}

export const marketplace = {
  attorneys: () =>
    request<MarketplaceAttorneyOut[]>("/v1/marketplace/attorneys"),
};

export const engagements = {
  list: () => request<EngagementOut[]>("/v1/engagements"),
  get: (id: string) => request<EngagementOut>(`/v1/engagements/${id}`),
  request: (body: {
    application_id: string;
    attorney_tenant_slug: string;
    scholar_message?: string | null;
  }) =>
    request<EngagementOut>("/v1/engagements", { method: "POST", body }),
  propose: (
    id: string,
    body: { attorney_proposal: string; fee_quote_usd: number },
  ) =>
    request<EngagementOut>(`/v1/engagements/${id}/propose`, {
      method: "POST",
      body,
    }),
  accept: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/accept`, {
      method: "POST",
    }),
  start: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/start`, {
      method: "POST",
    }),
  file: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/file`, {
      method: "POST",
    }),
  decline: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/decline`, {
      method: "POST",
    }),
  withdraw: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/withdraw`, {
      method: "POST",
    }),
  workspace: (id: string) =>
    request<EngagementWorkspace>(`/v1/engagements/${id}/workspace`),
};

export interface EngagementWorkspace {
  engagement: {
    id: string;
    status: string;
    scholar_tenant_id: string;
    attorney_tenant_id: string;
    scholar_message: string | null;
    attorney_proposal: string | null;
    attorney_fee_quote_cents: number | null;
  };
  application: {
    id: string;
    application_type: string;
    status: string;
    title: string;
  };
  scholar: {
    full_name: string | null;
    field_of_study: string | null;
    current_institution: string | null;
  } | null;
  // Present only on ACCEPTED+ engagements:
  evidence?: Array<{
    id: string;
    kind: string;
    label: string;
    file_name: string;
    size_bytes: number;
    parse_status: string;
  }>;
  criterion_assessments?: Array<{
    criterion_key: string;
    score: string;
    rationale: string | null;
  }>;
  petition_letter?: {
    status: string;
    body_markdown: string | null;
  } | null;
  recommendation_letters?: Array<{
    id: string;
    recommender_name: string | null;
    status: string;
    body_markdown: string | null;
  }>;
}

export interface DraftEventOut {
  id: string;
  application_id: string;
  kind: string;
  letter_id: string | null;
  outcome: "success" | "demo_stub" | "error";
  metadata_json: Record<string, unknown>;
  body_length: number;
  error_message: string | null;
  user_id: string;
  created_at: string;
}

export const audit = {
  draftEvents: (applicationId: string) =>
    request<DraftEventOut[]>(
      `/v1/applications/${applicationId}/draft-events`,
    ),
};

export function packetDownloadUrl(applicationId: string): string {
  return `${API_BASE_URL}/v1/applications/${applicationId}/packet`;
}

export function streamingUrl(path: string): string {
  // EventSource can't set Authorization headers, so we put the JWT in
  // the query string for SSE endpoints. The drafting router validates
  // it the same way as the header path.
  const token = getToken();
  const sep = path.includes("?") ? "&" : "?";
  return `${API_BASE_URL}${path}${sep}access_token=${encodeURIComponent(token ?? "")}`;
}

export const apiBaseUrl = API_BASE_URL;

// Dev-only helpers (POST /v1/dev/reset). Returns 404 in production.
export const dev = {
  reset: () => request<{ reset: boolean }>("/v1/dev/reset", { method: "POST" }),
};
